import type { IMTokens } from '@/interface/IMTokens';
import { LeagueSize, SportType, TokenVersion } from '@/interface/enum';
import { useOddsApiStore } from '@/store/oddsApiStore';

type OddsTokens = {
    soccerMOddsTokens: IMTokens;
    othersMOddsTokens: IMTokens;
};

type TokenResult = {
    version: TokenVersion;
    token: string;
};

const othersLeagueSizeTokenVersion: Record<LeagueSize, TokenVersion> = {
    [LeagueSize.Big]: TokenVersion.OB,
    [LeagueSize.Medium]: TokenVersion.OM,
    [LeagueSize.Small]: TokenVersion.OS,
};

const soccerLeagueSizeTokenVersion: Record<LeagueSize, TokenVersion> = {
    [LeagueSize.Big]: TokenVersion.SB,
    [LeagueSize.Medium]: TokenVersion.SM,
    [LeagueSize.Small]: TokenVersion.SS,
};

export function useOddsToken() {
    function getOddsTokenByVersion(version: TokenVersion): TokenResult {
        const tokens = getTokens();

        const tokenMap: Record<TokenVersion, string> = {
            [TokenVersion.SM]: tokens.soccerMOddsTokens.m,
            [TokenVersion.SB]: tokens.soccerMOddsTokens.b,
            [TokenVersion.SS]: tokens.soccerMOddsTokens.s,
            [TokenVersion.OM]: tokens.othersMOddsTokens.m,
            [TokenVersion.OB]: tokens.othersMOddsTokens.b,
            [TokenVersion.OS]: tokens.othersMOddsTokens.s,
        };

        const token = tokenMap[version];

        return { token, version };
    }

    function getOddsToken(sportType: SportType, leagueSize: LeagueSize): TokenResult {
        const leagueSizeConfig = getLeagueSizeTokenVersion(sportType);
        return getOddsTokenByVersion(leagueSizeConfig[leagueSize]);
    }

    function getLeagueSizeTokenVersion(sportType: SportType): Record<LeagueSize, TokenVersion> {
        return sportType === SportType.Soccer ? soccerLeagueSizeTokenVersion : othersLeagueSizeTokenVersion;
    }

    function getTokens(): OddsTokens {
        const oddsApiStore = useOddsApiStore();
        return {
            soccerMOddsTokens: oddsApiStore.soccerMOddsTokens,
            othersMOddsTokens: oddsApiStore.othersMOddsTokens,
        };
    }

    return {
        getOddsToken,
        getOddsTokenByVersion,
    };
}
